import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Brand, BrandMonogram } from "./Brand";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import {
  Sparkles, ShieldCheck, Zap, Wallet, BadgeCheck, ChevronDown, Menu,
  ArrowRight, UserCircle, LogOut, Lock, CreditCard,
} from "lucide-react";
import api, { formatEUR, formatPct, ASSET_ICONS } from "../lib/api";
import { AssetGlyph } from "./Brand";
import Marquee from "react-fast-marquee";

const FAQ = [
  { q: "How long does a SEPA transfer take?",
    a: "Most SEPA transfers settle within 1–2 hours during banking hours. Outside that window, the funds usually arrive the next business morning. As soon as we see the credit, your crypto is sent to your wallet automatically." },
  { q: "What are the fees?",
    a: "We charge a transparent 0.75% spread above the mid-market rate plus a 0.99% service fee. SEPA transfers have no extra cost; card payments include a 1.5% processor fee. The full breakdown is shown before you confirm." },
  { q: "Do I need to create an account?",
    a: "Yes — a free account lets us lock your rate, perform identity verification, and securely deliver your crypto. Sign-up takes under 30 seconds." },
  { q: "Which wallets are supported?",
    a: "Any self-custodied wallet: MetaMask, Phantom, Ledger, Trezor, Coinbase Wallet, Trust Wallet, or any address you control. We validate the format before you confirm." },
  { q: "What is the minimum purchase?",
    a: "€20 minimum, €25,000 maximum per order. Higher limits are available after enhanced verification." },
  { q: "Is Xchangely regulated?",
    a: "Xchangely operates a full compliance program: customer identity verification, sanctions screening, ongoing transaction monitoring, and source-of-funds checks are part of every account." },
];

export default function Homepage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [prices, setPrices] = useState([]);
  const [stats, setStats] = useState({ orders_total: 0, customers_total: 0 });
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get("/prices").then((r) => setPrices(r.data.prices || []));
    api.get("/stats").then((r) => setStats(r.data));
    const id = setInterval(() => {
      api.get("/prices").then((r) => setPrices(r.data.prices || [])).catch(() => {});
    }, 10000);
    return () => clearInterval(id);
  }, []);

  // All homepage buy CTAs route to the Buy Crypto experience (/buy).
  const BUY_ASSET_MAP = { BTC: "BTC", ETH: "ETH", USDT: "USDT_ERC20", USDC: "USDC_ERC20",
                           XRP: "XRP", ADA: "ADA", DOGE: "DOGE", MATIC: "MATIC" };
  const openBuy = (arg) => {
    const symbol = typeof arg === "string" ? arg : (arg && arg.asset) || null;
    const key = BUY_ASSET_MAP[symbol];
    navigate(key ? `/buy?asset=${key}` : "/buy");
  };

  return (
    <div className="min-h-screen bg-background" data-testid="homepage">
      <Header onBuy={() => openBuy()} user={user} logout={logout} navigate={navigate} />
      <Hero onBuy={() => openBuy()} prices={prices} stats={stats} />
      <Ticker prices={prices} />
      <ProductsSection navigate={navigate} />
      <HowItWorks />
      <AssetGrid prices={prices} onBuy={openBuy} />
      <WhyXchangely />
      <ForBusinessSection />
      <FaqSection faq={FAQ} openFaq={openFaq} setOpenFaq={setOpenFaq} />
      <FinalCta onBuy={() => openBuy()} />
      <Footer />
    </div>
  );
}

/* -------------------- Header -------------------- */
function Header({ onBuy, user, logout, navigate }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  // Close the sheet on route navigation so back/forward isn't trapped.
  useEffect(() => {
    if (!mobileOpen) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);
  const go = (path) => { setMobileOpen(false); navigate(path); };
  const goHash = (hash) => {
    setMobileOpen(false);
    setTimeout(() => { window.location.hash = hash; }, 100);
  };
  return (
    <header
      className={`xch-ink sticky top-0 z-30 transition-shadow ${scrolled ? "shadow-md border-b border-white/5" : ""}`}
      data-testid="home-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="home-logo-btn">
          <Brand size={28} theme="dark" />
        </button>
        <nav className="hidden md:flex items-center gap-1">
          <button
            onClick={() => navigate("/buy-crypto")}
            className="text-sm font-semibold text-white px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="home-nav-buy-crypto"
          >Buy Crypto</button>
          <NavLink href="#how">How it works</NavLink>
          <NavLink href="#assets">Assets</NavLink>
          <NavLink href="#why">Why us</NavLink>
          <NavLink href="#faq">FAQ</NavLink>
          <button
            onClick={() => navigate("/partner/embed")}
            className="text-sm text-white/70 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/5 transition-colors"
            data-testid="home-nav-partners"
          >Partners</button>
          <button
            onClick={() => navigate("/business")}
            className="text-sm text-white/70 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/5 transition-colors"
            data-testid="home-nav-business"
          >Xchangely Pay</button>
        </nav>
        <div className="flex items-center gap-2">
          {user ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 text-white hover:text-white hover:bg-white/10 rounded-xl" data-testid="home-user-menu-btn">
                  <UserCircle className="w-4 h-4" />
                  <span className="hidden sm:inline">{user.name?.split(" ")[0] || "Account"}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56 rounded-xl">
                <div className="px-2 py-1.5 border-b border-slate-100 mb-1.5">
                  <div className="font-display font-semibold text-sm text-foreground">{user.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                </div>
                <MenuItem onClick={() => navigate("/account")} testId="home-menu-account">Dashboard</MenuItem>
                <MenuItem onClick={() => navigate("/account/orders")} testId="home-menu-orders">My orders</MenuItem>
                <MenuItem onClick={() => navigate("/account/wallets")} testId="home-menu-wallets">Wallets</MenuItem>
                <MenuItem onClick={() => navigate("/account/settings")} testId="home-menu-settings">Settings</MenuItem>
                <div className="my-1.5 border-t border-slate-100" />
                <MenuItem onClick={() => navigate("/pay")} testId="home-menu-merchant">
                  <Sparkles className="w-3.5 h-3.5" /> Merchant sign in
                </MenuItem>
                <MenuItem onClick={() => { logout(); }} testId="home-menu-logout" danger>
                  <LogOut className="w-3.5 h-3.5" /> Sign out
                </MenuItem>
              </PopoverContent>
            </Popover>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="hidden sm:inline-block text-sm font-semibold text-white/85 hover:text-white px-3 py-2 rounded-xl hover:bg-white/5 transition-colors"
              data-testid="home-signin-btn"
            >Sign in</button>
          )}
          <Button
            onClick={onBuy}
            className="hidden sm:inline-flex bg-primary hover:bg-primary/90 text-white rounded-xl font-display font-semibold px-4 sm:px-5"
            data-testid="home-buy-btn"
          >Buy crypto</Button>
          {/* Hamburger — mobile only */}
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl text-white hover:bg-white/10 transition-colors"
            aria-label="Open menu"
            data-testid="home-mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>
      <MobileNavSheet
        open={mobileOpen} onClose={() => setMobileOpen(false)}
        user={user} logout={logout} onBuy={onBuy}
        go={go} goHash={goHash}
      />
    </header>
  );
}

function MobileNavSheet({ open, onClose, user, logout, onBuy, go, goHash }) {
  return (
    <>
      {/* Dim backdrop */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className={`md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      />
      {/* Slide-down sheet */}
      <div
        role="dialog"
        aria-modal="true"
        data-testid="home-mobile-sheet"
        className={`md:hidden fixed inset-x-0 top-0 z-50 xch-ink shadow-2xl border-b border-white/10
                     transition-transform duration-300 ease-out
                     ${open ? "translate-y-0" : "-translate-y-full"}`}
      >
        <div className="max-w-md mx-auto px-5 pt-4 pb-6">
          <div className="flex items-center justify-between mb-5">
            <Brand size={26} theme="dark" />
            <button
              onClick={onClose}
              aria-label="Close menu"
              className="w-9 h-9 rounded-xl flex items-center justify-center text-white hover:bg-white/10 transition-colors"
              data-testid="home-mobile-close-btn"
            >
              <ChevronDown className="w-5 h-5 rotate-180" />
            </button>
          </div>

          {user && (
            <div className="rounded-2xl bg-white/5 border border-white/10 px-4 py-3 mb-4">
              <div className="text-[10px] uppercase tracking-wider text-white/40 font-bold mb-0.5">Signed in</div>
              <div className="font-display font-semibold text-white text-sm">{user.name}</div>
              <div className="text-xs text-white/60 truncate">{user.email}</div>
            </div>
          )}

          <nav className="space-y-1 mb-5" data-testid="home-mobile-nav">
            <MSheetLink onClick={() => go("/buy-crypto")} testId="home-mobile-buy-crypto">Buy Crypto</MSheetLink>
            <MSheetLink onClick={() => goHash("#how")} testId="home-mobile-how">How it works</MSheetLink>
            <MSheetLink onClick={() => goHash("#assets")} testId="home-mobile-assets">Assets</MSheetLink>
            <MSheetLink onClick={() => goHash("#why")} testId="home-mobile-why">Why us</MSheetLink>
            <MSheetLink onClick={() => goHash("#faq")} testId="home-mobile-faq">FAQ</MSheetLink>
            <MSheetLink onClick={() => go("/partner/embed")} testId="home-mobile-partners">Partners</MSheetLink>
            <MSheetLink onClick={() => go("/business")} testId="home-mobile-business" icon={Sparkles}>
              Xchangely Pay
            </MSheetLink>
          </nav>

          {user ? (
            <div className="space-y-1 pt-4 border-t border-white/10">
              <MSheetLink onClick={() => go("/account")} testId="home-mobile-account">Dashboard</MSheetLink>
              <MSheetLink onClick={() => go("/account/orders")} testId="home-mobile-orders">My orders</MSheetLink>
              <MSheetLink onClick={() => go("/account/wallets")} testId="home-mobile-wallets">Wallets</MSheetLink>
              <MSheetLink onClick={() => go("/account/settings")} testId="home-mobile-settings">Settings</MSheetLink>
              <MSheetLink onClick={() => go("/pay")} testId="home-mobile-merchant" icon={Sparkles}>
                Merchant sign in
              </MSheetLink>
              <button
                onClick={() => { onClose(); logout(); }}
                className="w-full text-left text-sm font-semibold px-4 py-3 rounded-xl text-rose-300 hover:bg-rose-500/10 flex items-center gap-2 transition-colors"
                data-testid="home-mobile-logout"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              <Button
                onClick={() => { onClose(); onBuy(); }}
                className="bg-primary hover:bg-primary/90 text-white rounded-xl font-display font-semibold h-12 text-base"
                data-testid="home-mobile-buy-btn"
              >Buy crypto</Button>
              <Button
                onClick={() => go("/login")}
                variant="outline"
                className="bg-transparent border-white/20 text-white hover:bg-white/10 rounded-xl h-12 text-base"
                data-testid="home-mobile-signin-btn"
              >Sign in</Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const MSheetLink = ({ onClick, children, icon: Icon, testId }) => (
  <button
    onClick={onClick}
    className="w-full text-left text-base px-4 py-3 rounded-xl text-white/85 hover:bg-white/5 hover:text-white flex items-center gap-2 transition-colors"
    data-testid={testId}
  >
    {Icon && <Icon className="w-4 h-4 text-emerald-300" />}
    {children}
  </button>
);
const NavLink = ({ href, children }) => (
  <a href={href}
     className="text-sm text-white/70 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/5 transition-colors">
    {children}
  </a>
);
const MenuItem = ({ children, onClick, testId, danger }) => (
  <button onClick={onClick}
          className={`w-full text-left text-sm px-2 py-1.5 hover:bg-muted rounded-md transition-colors flex items-center gap-1.5 ${danger ? "text-destructive" : ""}`}
          data-testid={testId}>
    {children}
  </button>
);

/* -------------------- Hero -------------------- */
function Hero({ onBuy, prices, stats }) {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 xch-app-bg" />
      {/* Soft branded watermark drifts behind the hero copy */}
      <div className="absolute -z-10 pointer-events-none select-none top-[-60px] right-[-80px] opacity-[0.06] hidden sm:block" aria-hidden>
        <BrandMonogram size={520} />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-10 sm:pt-20 sm:pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="text-center lg:text-left"
        >
          <div className="xch-eyebrow mb-4" data-testid="hero-eyebrow">
            <Sparkles className="w-3 h-3" /> A transparent crypto on-ramp
          </div>
          <h1 className="xch-headline text-[2.6rem] sm:text-[3.6rem] lg:text-[4.2rem]" data-testid="hero-headline">
            Buy crypto.<br /><span className="text-primary">Skip the hidden fees.</span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground mt-5 max-w-xl mx-auto lg:mx-0">
            PayID, Interac e-Transfer or card. Fiat to crypto on bank-grade rails —
            no surprises, no padded spreads, just the rate you see.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-7 justify-center lg:justify-start">
            <Button onClick={onBuy}
                    className="bg-primary hover:bg-primary/90 text-white rounded-xl h-12 font-display font-semibold px-6 text-base transition-transform hover:-translate-y-0.5 hover:shadow-lg"
                    data-testid="hero-buy-btn">
              Buy crypto now <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button asChild variant="ghost"
                    className="rounded-xl h-12 font-display font-semibold px-5 text-base text-foreground hover:bg-muted">
              <a href="#how" data-testid="hero-how-btn">See how it works</a>
            </Button>
          </div>
          <div className="mt-7 flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-primary" />
              <strong className="text-foreground font-semibold">Compliance-first platform</strong></span>
            <span>·</span>
            <span><strong className="text-foreground font-mono tabular-nums">{(stats.customers_total || 0).toLocaleString()}</strong>+ users</span>
            <span>·</span>
            <span>10 assets across <strong className="text-foreground">8 networks</strong></span>
          </div>
        </motion.div>

        {/* Hero visual: mini live-price card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="lg:justify-self-end w-full max-w-md mx-auto"
          data-testid="hero-visual"
        >
          <HeroWidgetMock onBuy={onBuy} prices={prices} />
        </motion.div>
      </div>
    </section>
  );
}

function HeroWidgetMock({ onBuy, prices }) {
  const usdc = prices.find((p) => p.symbol === "USDC");
  const usdt = prices.find((p) => p.symbol === "USDT");
  const btc = prices.find((p) => p.symbol === "BTC");
  const eth = prices.find((p) => p.symbol === "ETH");
  // Default hero asset is USDC — XGate's flagship rail and the asset that
  // actually drives our 3-leg float-funded settlement.
  const hero = usdc || usdt || btc;
  const heroSym = hero === usdc ? "USDC" : hero === usdt ? "USDT" : "BTC";
  const heroColor = heroSym === "USDC" ? "#2775CA" : heroSym === "USDT" ? "#26A17B" : "#F7931A";
  return (
    <div className="xch-card p-5 sm:p-6 relative">
      <div className="absolute -top-3 left-5 px-2.5 py-1 bg-foreground text-white rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live preview
      </div>
      <div className="space-y-3">
        <div className="bg-muted/40 rounded-2xl p-3.5">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">You pay</div>
          <div className="flex items-baseline gap-1">
            <span className="font-display text-3xl font-extrabold text-foreground">€500</span>
            <span className="text-xs text-muted-foreground ml-auto">EUR</span>
          </div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-3.5">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">You receive</div>
          <div className="flex items-center gap-2">
            <span className="font-display text-3xl font-extrabold text-foreground tabular-nums">
              {hero ? (500 / hero.price_eur * 0.9876).toFixed(2) : "528.45"}
            </span>
            <span className="ml-auto flex items-center gap-1.5 bg-muted px-2.5 py-1 rounded-full text-xs font-bold">
              <AssetGlyph symbol={heroSym} color={heroColor} size={20} /> {heroSym}
            </span>
          </div>
          {hero && (
            <div className="text-[11px] text-muted-foreground mt-1.5 font-mono">
              1 {heroSym} ≈ {formatEUR(hero.price_eur)}
            </div>
          )}
        </div>
        <Button onClick={() => onBuy && onBuy({ asset: heroSym })}
                className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl h-11 font-display font-semibold transition-transform hover:-translate-y-0.5">
          Get this quote
        </Button>
        <div className="flex items-center justify-around text-[10px] text-muted-foreground pt-1.5">
          <Mini name="USDC" data={usdc} highlight={heroSym === "USDC"} />
          <Mini name="USDT" data={usdt} highlight={heroSym === "USDT"} />
          <Mini name="BTC" data={btc} />
          <Mini name="ETH" data={eth} />
        </div>
      </div>
    </div>
  );
}
function Mini({ name, data, highlight }) {
  if (!data) return <div className="w-16 h-6" />;
  const up = (data.change_24h || 0) >= 0;
  return (
    <div className={`flex flex-col items-center ${highlight ? "scale-105" : "opacity-60"}`}>
      <span className="font-display font-bold text-foreground">{name}</span>
      <span className={`font-mono ${up ? "text-emerald-600" : "text-rose-600"}`}>{formatPct(data.change_24h)}</span>
    </div>
  );
}

/* -------------------- Live ticker bar -------------------- */
function Ticker({ prices }) {
  if (!prices.length) return null;
  return (
    <div className="xch-ink py-3 border-y border-white/5" data-testid="home-ticker">
      <Marquee pauseOnHover speed={42} gradient={false}>
        {prices.map((p) => {
          const up = (p.change_24h || 0) >= 0;
          return (
            <div key={p.symbol} className="flex items-center gap-2.5 px-6">
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white"
                    style={{ backgroundColor: ASSET_ICONS[p.symbol] || "#4F46FF" }}>
                {p.symbol.slice(0, 3)}
              </span>
              <span className="font-display font-bold text-sm text-white">{p.symbol}</span>
              <span className="font-mono text-sm tabular-nums text-white/90">{formatEUR(p.price_eur)}</span>
              <span className={`font-mono text-xs font-semibold ${up ? "xch-ticker-up" : "xch-ticker-down"}`}>
                {up ? "▲" : "▼"} {formatPct(p.change_24h)}
              </span>
            </div>
          );
        })}
      </Marquee>
    </div>
  );
}

/* -------------------- How it works -------------------- */
function HowItWorks() {
  const steps = [
    {
      n: 1,
      title: "Pick your crypto",
      desc: "Choose from 10+ assets. Enter the EUR amount you want to spend, pick the receiving network, and paste your wallet address — we never hold custody.",
    },
    {
      n: 2,
      title: "Verify & pay via our partner",
      desc: "We send you to XGate — our regulated payment partner — to verify your identity (one time) and get your unique deposit instructions. Send your SEPA transfer using the details they show you, then return here.",
    },
    {
      n: 3,
      title: "We deliver to your wallet",
      desc: "Once your payment lands, we send the crypto to the address you gave us. Most orders complete within a few hours; larger orders may need extra verification. You'll get an on-chain transaction id when it's sent.",
    },
  ];
  return (
    <section id="how" className="py-16 sm:py-24 bg-[#F0F0EA]/40" data-testid="home-how">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="max-w-2xl mx-auto text-center mb-12">
          <div className="xch-eyebrow mb-3">How it works</div>
          <h2 className="xch-headline text-3xl sm:text-4xl">Three steps. Crypto delivered to your wallet.</h2>
          <p className="text-sm text-muted-foreground mt-3" data-testid="how-it-works-timing-note">
            Most orders complete within a few hours. Larger orders or first-time customers may need extra verification — we'll keep you updated by email.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {steps.map((s) => (
            <div key={s.n} className="xch-card p-6" data-testid={`how-step-${s.n}`}>
              <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-display font-extrabold text-lg mb-4">
                {s.n}
              </div>
              <h3 className="font-display text-xl font-bold text-foreground mb-1">{s.title}</h3>
              <p className="text-sm text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="max-w-3xl mx-auto mt-10 rounded-2xl border border-border bg-card p-4 sm:p-5 text-sm text-foreground/90"
             data-testid="how-it-works-redirect-note">
          <div className="font-display font-bold text-foreground mb-1">Why am I being sent to another site?</div>
          <p className="text-muted-foreground">
            XGate is the regulated EMI we use to handle your SEPA payment and identity verification. Sending you to them keeps your bank details out of our hands and meets EU EMI regulations. You'll come straight back to us once XGate confirms the payment, and we'll handle the crypto delivery from there.
          </p>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Asset grid -------------------- */
function AssetGrid({ prices, onBuy }) {
  return (
    <section id="assets" className="py-16 sm:py-24" data-testid="home-assets">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="xch-eyebrow mb-3">Assets</div>
            <h2 className="xch-headline text-3xl sm:text-4xl">{prices.length}+ assets available</h2>
            <p className="text-sm text-muted-foreground mt-2">Live prices from CoinGecko. Updated every 10 seconds.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="asset-grid">
          {prices.map((p) => {
            const up = (p.change_24h || 0) >= 0;
            return (
              <button key={p.symbol} onClick={() => onBuy(p.symbol)}
                      className="xch-card p-4 text-left hover:-translate-y-0.5 hover:shadow-lg transition-all group"
                      data-testid={`asset-card-${p.symbol}`}>
                <div className="flex items-center gap-2.5 mb-3">
                  <AssetGlyph symbol={p.symbol} color={ASSET_ICONS[p.symbol] || "#4F46FF"} size={32} />
                  <div className="min-w-0">
                    <div className="font-display font-bold text-foreground truncate">{p.symbol}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{p.name}</div>
                  </div>
                </div>
                <div className="font-mono text-sm font-bold text-foreground tabular-nums">{formatEUR(p.price_eur)}</div>
                <div className="flex items-center justify-between mt-1.5">
                  <span className={`text-[11px] font-mono font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`}>
                    {up ? "▲" : "▼"} {formatPct(p.change_24h)}
                  </span>
                  <span className="text-xs font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                    Buy →
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------- Why Xchangely -------------------- */
function WhyXchangely() {
  const features = [
    { icon: BadgeCheck, title: "Transparent fees", desc: "Full fee breakdown before you confirm. No hidden spread. No surprises." },
    { icon: Zap, title: "Honest timing", desc: "SEPA transfers usually settle in 1–2 banking hours; cards are instant. Larger orders may need extra verification — we'll tell you upfront." },
    { icon: Wallet, title: "Self-custody", desc: "Crypto goes straight to your wallet. We never hold customer assets." },
    { icon: ShieldCheck, title: "Compliance-first", desc: "Identity verification, sanctions screening, and ongoing transaction monitoring on every account." },
  ];
  return (
    <section id="why" className="py-16 sm:py-24 bg-[#F0F0EA]/40" data-testid="home-why">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="max-w-2xl mx-auto text-center mb-12">
          <div className="xch-eyebrow mb-3">Why Xchangely</div>
          <h2 className="xch-headline text-3xl sm:text-4xl">Built for honest pricing.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f, i) => (
            <div key={i} className="xch-card p-5" data-testid={`why-card-${i}`}>
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-display text-lg font-bold text-foreground">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-1.5">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* -------------------- For business (Xchangely Pay) -------------------- */
function ForBusinessSection() {
  const navigate = useNavigate();
  return (
    <section id="business" className="py-16 sm:py-24 bg-foreground text-white" data-testid="home-business">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Headline column */}
          <div className="lg:col-span-5">
            <div className="xch-eyebrow mb-3 text-emerald-300">
              <Sparkles className="w-3 h-3 inline mr-1" /> Xchangely Pay · for businesses
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mb-5">
              Accept any card.<br />
              <span className="text-emerald-300">Settle in stablecoin.</span>
            </h2>
            <p className="text-base text-white/70 leading-relaxed mb-6 max-w-md">
              We turn your customer&apos;s card payment into USDC or USDT in your wallet — within minutes.
              No reconciliation, no FX risk, no chargeback liability. Built for B2B.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => navigate("/business")}
                       className="bg-emerald-500 hover:bg-emerald-400 text-foreground rounded-xl font-bold gap-1.5"
                       data-testid="home-business-cta">
                Become a merchant
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button onClick={() => navigate("/business/docs")} variant="outline"
                       className="bg-transparent border-white/20 text-white hover:bg-white/5 rounded-xl"
                       data-testid="home-business-docs-cta">
                Read the API docs
              </Button>
            </div>
          </div>

          {/* 3 mini-cards column */}
          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-3">
            <BizStep
              n="01"
              title="Send a payment link"
              body="Generate a one-click checkout link from your dashboard or via REST API. Embed it in invoices, emails, or your storefront."
              testId="biz-step-1"
            />
            <BizStep
              n="02"
              title="Customer pays with card"
              body="They tap Apple Pay, Google Pay, or any Visa/Mastercard. PCI-DSS Level 1 checkout. KYC handled for you."
              testId="biz-step-2"
            />
            <BizStep
              n="03"
              title="You receive USDC/USDT"
              body="Funds settle to your wallet automatically. Webhooks confirm in real time. Reporting and reconciliation included."
              testId="biz-step-3"
            />
          </div>
        </div>

        {/* Trust strip */}
        <div className="mt-12 pt-8 border-t border-white/10 grid sm:grid-cols-4 gap-6 text-sm text-white/60">
          <BizTrust icon={Lock}        text="PCI-DSS Level 1 partner" />
          <BizTrust icon={ShieldCheck} text="EU-licensed operator" />
          <BizTrust icon={Sparkles}    text="Instant settlement" />
          <BizTrust icon={CreditCard}  text="Cards · iDEAL · Bancontact · Apple Pay" />
        </div>
      </div>
    </section>
  );
}

const BizStep = ({ n, title, body, testId }) => (
  <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 hover:bg-white/[0.06] transition-colors"
        data-testid={testId}>
    <div className="text-emerald-300 font-mono text-[11px] tracking-wider mb-2">{n}</div>
    <div className="font-display font-bold text-base mb-1.5">{title}</div>
    <div className="text-xs text-white/60 leading-relaxed">{body}</div>
  </div>
);

const BizTrust = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-2">
    <Icon className="w-4 h-4 text-emerald-300 shrink-0" />
    <span className="text-xs">{text}</span>
  </div>
);


/* -------------------- FAQ -------------------- */
function FaqSection({ faq, openFaq, setOpenFaq }) {
  return (
    <section id="faq" className="py-16 sm:py-24" data-testid="home-faq">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <div className="xch-eyebrow mb-3">FAQ</div>
          <h2 className="xch-headline text-3xl sm:text-4xl">Frequently asked questions</h2>
        </div>
        <div className="space-y-2">
          {faq.map((item, i) => {
            const open = openFaq === i;
            return (
              <div key={i} className="xch-card overflow-hidden" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpenFaq(open ? -1 : i)}
                  className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left hover:bg-muted/30 transition-colors"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-display font-semibold text-foreground">{item.q}</span>
                  <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                  <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border/50 pt-3" data-testid={`faq-answer-${i}`}>
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------- Final CTA -------------------- */
function FinalCta({ onBuy }) {
  return (
    <section className="py-16 sm:py-20" data-testid="home-final-cta">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="xch-ink rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-30"
               style={{ background: "radial-gradient(ellipse at top, rgba(79,70,255,0.6), transparent 60%)" }} />
          <div className="relative">
            <h2 className="xch-headline text-3xl sm:text-4xl text-white">Ready to buy crypto the honest way?</h2>
            <p className="text-white/70 mt-3 max-w-md mx-auto">No accounts to fund. No locked balances. Just SEPA or card to your wallet.</p>
            <Button onClick={onBuy}
                    className="bg-primary hover:bg-primary/90 text-white rounded-xl h-12 px-7 font-display font-semibold mt-6 transition-transform hover:-translate-y-0.5"
                    data-testid="final-cta-btn">
              Buy crypto now <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Footer -------------------- */
function Footer() {
  return (
    <footer className="bg-foreground text-white py-12" data-testid="home-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <Brand size={26} theme="dark" />
            <p className="text-sm text-white/60 mt-3 max-w-xs">A transparent crypto on-ramp.<br />SEPA or card to your own wallet — never custodied by us.</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-white/40 mb-3">Product</div>
            <ul className="space-y-2 text-sm">
              <li><a href="#how" className="text-white/80 hover:text-white">How it works</a></li>
              <li><a href="#faq" className="text-white/80 hover:text-white">FAQ</a></li>
              <li><a href="#assets" className="text-white/80 hover:text-white">Supported assets</a></li>
              <li><a href="/partner/embed" className="text-white/80 hover:text-white">Partner program</a></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-white/40 mb-3">Business</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/business" className="text-white/80 hover:text-white" data-testid="home-footer-accept-payments">Xchangely Pay</Link></li>
              <li><Link to="/pay" className="text-white/80 hover:text-white" data-testid="home-footer-merchant-signin">Merchant sign in</Link></li>
              <li><a href="mailto:business@xchangely.io" className="text-white/80 hover:text-white" data-testid="home-footer-business-contact">business@xchangely.io</a></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-white/40 mb-3">Legal</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/legal/terms" className="text-white/80 hover:text-white" data-testid="home-footer-terms">Terms of Service</Link></li>
              <li><Link to="/legal/privacy" className="text-white/80 hover:text-white" data-testid="home-footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/legal/aml" className="text-white/80 hover:text-white" data-testid="home-footer-aml">AML / KYC Disclosure</Link></li>
              <li><Link to="/legal/why-sof" className="text-white/80 hover:text-white" data-testid="home-footer-why-sof">Why we ask for Source of Funds</Link></li>
              <li><Link to="/legal/cookies" className="text-white/80 hover:text-white" data-testid="home-footer-cookies">Cookie Notice</Link></li>
              <li><Link to="/support" className="text-white/80 hover:text-white" data-testid="home-footer-support">Help &amp; Support</Link></li>
            </ul>
          </div>
        </div>
        <div className="pt-6 border-t border-white/10 text-xs text-white/40 leading-relaxed" data-testid="home-footer-compliance">
          <p>
            Xchangely — a virtual-currency on-ramp · Support: support@xchangely.io
          </p>
          <p className="mt-1.5">
            The virtual IBAN and crypto swap are facilitated through our regulated payment partner. This service does not provide investment advice; crypto-asset values can go down as well as up.
          </p>
        </div>
        <div className="pt-3 text-xs text-white/40 flex flex-wrap items-center justify-between gap-2">
          <div>© {new Date().getFullYear()} Xchangely. All rights reserved.</div>
          <div>Crypto purchases are non-refundable.</div>
        </div>
      </div>
    </footer>
  );
}

/* -------------------- Products -------------------- */
function ProductsSection({ navigate }) {
  const products = [
    {
      key: "buy-crypto", icon: Wallet, badge: "New",
      title: "Buy Crypto",
      desc: "Purchase BTC, ETH, stablecoins and more with PayID, Interac or card — settled to your Xchangely account.",
      cta: "Start Buying", to: "/buy-crypto",
    },
    {
      key: "gateway", icon: CreditCard, badge: null,
      title: "Payment Gateway",
      desc: "Accept card and local payments and settle in USDC with Xchangely Pay — links, checkout and webhooks for your business.",
      cta: "Explore Xchangely Pay", to: "/business",
    },
    {
      key: "partner", icon: Sparkles, badge: null,
      title: "Partner Widget",
      desc: "Embed the Xchangely on-ramp in your product with a white-labelled widget and revenue share.",
      cta: "Become a partner", to: "/partner/embed",
    },
  ];
  return (
    <section className="border-t border-border" data-testid="home-products">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="xch-eyebrow mb-3">Products</div>
        <h2 className="font-display font-bold text-base md:text-lg mb-8">One platform, every crypto rail</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {products.map((p) => (
            <div key={p.key} className="xch-card p-6 flex flex-col" data-testid={`home-product-${p.key}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <p.icon className="w-5 h-5 text-primary" />
                </div>
                {p.badge && (
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground rounded-full px-2 py-0.5">{p.badge}</span>
                )}
              </div>
              <div className="font-display font-semibold">{p.title}</div>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed flex-1">{p.desc}</p>
              <Button variant="outline" size="sm" className="rounded-xl gap-1.5 mt-5 self-start"
                onClick={() => navigate(p.to)} data-testid={`home-product-cta-${p.key}`}>
                {p.cta} <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

